import { z } from 'zod'

import { label } from '#admin/features/billing/api.ts'
import { api } from '#admin/lib/api-client.ts'

const historySchema = z.object({
  items: z.array(
    z.object({
      id: z.string(),
      action: z.string(),
      actorName: z.string().nullable(),
      createdAt: z.string(),
      note: z.string().nullable(),
    }),
  ),
})

const base = '/v1/billing/admin'

export function fetchHistory(kind: 'invoices' | 'payments', id: string, signal?: AbortSignal) {
  return api.get(`${base}/${kind}/${id}/history`, {
    schema: historySchema,
    ...(signal ? { signal } : {}),
  })
}

/** The audit actions an invoice's or a payment's history carries. Anything else reads as its own name. */
const HISTORY_LABELS: Record<string, string> = {
  'billing.invoice_created': 'Draft created',
  'billing.invoice_updated': 'Draft edited',
  'billing.invoice_issued': 'Issued',
  'billing.invoice_voided': 'Voided',
  'billing.invoice_reissued': 'Reissued as a new draft',
  'billing.invoice_replaced': 'Replaced by a new invoice',
  'billing.invoice_deleted': 'Draft discarded',
  'billing.invoice_emailed': 'Emailed',
  'billing.invoice_email_failed': 'Email failed',
  'billing.invoice_email_rejected': 'Email not sent',
  'billing.invoice_issue_rejected': 'Issue refused',
  'billing.invoice_void_rejected': 'Void refused',
  'billing.invoice_update_rejected': 'Edit refused',
  'billing.invoice_delete_rejected': 'Discard refused',
  'billing.invoice_reissue_rejected': 'Reissue refused',
  'billing.invoice_replace_rejected': 'Replacement refused',
  'billing.company_invoice_marked_paid': 'Company invoice marked paid',
  'billing.company_invoice_marked_unpaid': 'Company invoice marked unpaid',
  'billing.payment_recorded': 'Payment recorded',
  'billing.payment_confirmed': 'Payment confirmed received',
  'billing.payment_rejected': 'Payment marked failed',
  'billing.payment_confirm_rejected': 'Payment confirmation refused',
  'billing.payment_reject_rejected': 'Payment failure refused',
  'billing.payment_allocated': 'Payment allocated',
  'billing.payment_voided': 'Payment voided',
  'billing.payment_void_rejected': 'Void refused',
  'billing.payment_proof_attached': 'Proof of payment attached',
  'billing.credit_note_created': 'Credit note drafted',
  'billing.credit_note_updated': 'Credit note edited',
  'billing.credit_note_issued': 'Credit note issued',
  'billing.credit_note_voided': 'Credit note voided',
  'billing.credit_note_create_rejected': 'Credit note refused',
  'billing.credit_note_update_rejected': 'Credit note edit refused',
  'billing.credit_note_issue_rejected': 'Credit note issue refused',
  'billing.credit_note_void_rejected': 'Credit note void refused',
  // Written by `ticket` in the same transaction as the invoice, so the invoice history shows it.
  'ticket.invoice_created': 'Raised for a registration',
  'ticket.invoice_recreated': 'Replaced from the registration',
  'ticket.payment_confirm_rejected': 'Payment on the registration refused',
  'exhibitor.invoice_recreated': 'Replaced from the exhibitor application',
}

export function historyLabel(action: string): string {
  const named = HISTORY_LABELS[action]
  if (named !== undefined) return named
  const words = label(action.replace(/^billing\./, ''))
  return words.charAt(0).toUpperCase() + words.slice(1)
}
